import { useState } from 'react';
import { Box, Button, Chip, Snackbar, Typography } from '@mui/material';
import { Delete, RestartAlt, Tune } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client/react';
import ConfirmDialog from '../../components/ConfirmDialog';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatDate } from '../../Formatters';
import { DELETE_USAGE_LIMIT, GET_USAGE_LIMITS, GET_USER_USAGE_STATUS, RESET_USER_USAGE } from '../../usagelimits/usagelimits.graphql';
import { RuleDialog } from '../../usagelimits/RuleDialog';
import { productName } from '../../usagelimits/products';
import { ProductUsageStatus, UsageLimitRule, UserUsageStatus } from '../../usagelimits/interfaces';

interface Props {
  userId: number;
}

export function UsagePanel({ userId }: Props) {
  const usageQuery = useQuery<{ usageStatus: UserUsageStatus }>(GET_USER_USAGE_STATUS, { variables: { userId } });
  const limitsQuery = useQuery<{ usageLimits: UsageLimitRule[] }>(GET_USAGE_LIMITS);
  const [confirmReset, setConfirmReset] = useState<{ sourceId: number; metric: 'documents' | 'aitokens' } | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<UsageLimitRule | null>(null);
  const [limitDialogSourceId, setLimitDialogSourceId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // The reset is applied by statistics' async projection, so the fresh numbers lag by a few
  // seconds — refetch once more after a short delay instead of relying on refetchQueries.
  const [resetUsage, resetState] = useMutation<any, any>(RESET_USER_USAGE);
  const [deleteRule, deleteState] = useMutation<any, any>(DELETE_USAGE_LIMIT, { refetchQueries: [{ query: GET_USAGE_LIMITS }] });

  if (usageQuery.error)
    return (
      <Box sx={{ p: 3 }}>
        <span>Feil! {usageQuery.error.message}</span>
      </Box>
    );
  if (usageQuery.loading || !usageQuery.data) return <LoadingSpinner />;

  const products = usageQuery.data.usageStatus.products ?? [];
  // Null-source rules predate product scoping and apply to both products — without matching them
  // here, "Sett personlig grense" would create a duplicate next to a rule that is already in effect.
  const userRuleFor = (sourceId: number) =>
    (limitsQuery.data?.usageLimits ?? []).find(
      (rule) => rule.userId === userId && rule.active && (rule.sourceId === sourceId || rule.sourceId == null),
    ) ?? null;

  const dialogRule = limitDialogSourceId != null ? userRuleFor(limitDialogSourceId) : null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Forbruk i den nye portalen per produkt, målt mot gjeldende bruksgrenser. Endringer trer i kraft i portalen innen et par minutter.
      </Typography>

      {products.map((product) => (
        <ProductSection
          key={product.sourceId}
          product={product}
          userRule={userRuleFor(product.sourceId)}
          resetting={resetState.loading}
          deleting={deleteState.loading}
          onReset={(metric) => setConfirmReset({ sourceId: product.sourceId, metric })}
          onEditLimit={() => setLimitDialogSourceId(product.sourceId)}
          onDeleteLimit={(rule) => setRuleToDelete(rule)}
        />
      ))}

      <ConfirmDialog
        open={confirmReset != null}
        title={`Nullstill ${confirmReset?.metric === 'aitokens' ? 'KI-søk' : 'anvisninger'} – ${productName(confirmReset?.sourceId)}`}
        message="Tellingen for denne målingen starter på nytt fra nå. Den andre målingen og grensene beholdes."
        confirmText="Nullstill"
        confirm={async () => {
          await resetUsage({ variables: { input: { userId, sourceId: confirmReset!.sourceId, metric: confirmReset!.metric } } });
          setConfirmReset(null);
          setSnackbar('Bruken er nullstilt. Tallene oppdateres i løpet av noen sekunder.');
          void usageQuery.refetch();
          setTimeout(() => void usageQuery.refetch(), 3000);
        }}
        deny={() => setConfirmReset(null)}
      />

      <ConfirmDialog
        open={!!ruleToDelete}
        title="Slett personlig grense"
        message="Er du sikker på at du vil slette den personlige grensen? Bredere regler (lisens, abonnement, global) gjelder fortsatt."
        confirmText="Slett"
        confirm={async () => {
          await deleteRule({ variables: { input: { id: ruleToDelete!.id } } });
          setRuleToDelete(null);
          setSnackbar('Den personlige grensen er slettet.');
          void usageQuery.refetch();
        }}
        deny={() => setRuleToDelete(null)}
      />

      {limitDialogSourceId != null && (
        <RuleDialog
          open
          onClose={() => setLimitDialogSourceId(null)}
          rule={dialogRule}
          presetUserId={userId}
          presetSourceId={limitDialogSourceId}
          onSaved={() => {
            usageQuery.refetch();
            setSnackbar('Bruksgrensen er lagret.');
          }}
        />
      )}

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ''}
      />
    </Box>
  );
}

const SCOPE_LABELS: Record<string, string> = { User: 'Bruker', License: 'Lisens', Subscription: 'Abonnement' };

function ProductSection({
  product,
  userRule,
  resetting,
  deleting,
  onReset,
  onEditLimit,
  onDeleteLimit,
}: {
  product: ProductUsageStatus;
  userRule: UsageLimitRule | null;
  resetting: boolean;
  deleting: boolean;
  onReset: (metric: 'documents' | 'aitokens') => void;
  onEditLimit: () => void;
  onDeleteLimit: (rule: UsageLimitRule) => void;
}) {
  return (
    <Box sx={{ mb: 3, border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'fontWeightMedium' }}>
          {productName(product.sourceId)}
        </Typography>
        {product.inEffectRuleScope ? (
          <Chip
            size="small"
            color={product.inEffectRuleScope === 'User' ? 'primary' : 'default'}
            label={`Gjeldende regel: ${SCOPE_LABELS[product.inEffectRuleScope] ?? product.inEffectRuleScope}`}
          />
        ) : (
          <Chip size="small" variant="outlined" label="Ingen regel" />
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <StatCard
          title="Anvisninger"
          used={product.documentsUsed}
          max={product.maxDocuments}
          windowDays={product.documentWindowDays}
          lastResetAt={product.lastDocumentResetAt}
          resetting={resetting}
          onReset={() => onReset('documents')}
        />
        <StatCard
          title="KI-tokens"
          used={product.aiTokensUsed}
          max={product.maxAiTokens}
          windowDays={product.aiTokenWindowDays}
          lastResetAt={product.lastAiResetAt}
          resetting={resetting}
          onReset={() => onReset('aitokens')}
        />
      </Box>

      {globalLimitText(product) && (
        <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
          Global grense (håndheves alltid): {globalLimitText(product)}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
        <Button variant="contained" color="primary" size="small" startIcon={<Tune />} onClick={onEditLimit}>
          {userRule ? 'Endre personlig grense' : 'Sett personlig grense'}
        </Button>
        {userRule && (
          <Button variant="outlined" size="small" color="error" startIcon={<Delete />} onClick={() => onDeleteLimit(userRule)} disabled={deleting}>
            Slett personlig grense
          </Button>
        )}
      </Box>
    </Box>
  );
}

function globalLimitText(product: ProductUsageStatus): string | null {
  const parts: string[] = [];
  if (product.globalMaxDocuments != null)
    parts.push(`${product.globalMaxDocuments.toLocaleString('nb-NO')} anvisninger / ${product.globalDocumentWindowDays} dager`);
  if (product.globalMaxAiTokens != null)
    parts.push(`${product.globalMaxAiTokens.toLocaleString('nb-NO')} KI-tokens / ${product.globalAiTokenWindowDays} dager`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function StatCard({
  title,
  used,
  max,
  windowDays,
  lastResetAt,
  resetting,
  onReset,
}: {
  title: string;
  used: number;
  max?: number | null;
  windowDays: number;
  lastResetAt?: string | null;
  resetting: boolean;
  onReset: () => void;
}) {
  const overLimit = max != null && used >= max;
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, px: 2, py: 1.5, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Button variant="outlined" size="small" startIcon={<RestartAlt />} onClick={onReset} disabled={resetting}>
          Nullstill
        </Button>
      </Box>
      <Typography variant="h5" color={overLimit ? 'error' : 'text.primary'} sx={{ fontWeight: 'fontWeightMedium', mt: 0.5 }}>
        {used.toLocaleString('nb-NO')}
        {max != null && (
          <Typography component="span" variant="body1" color="text.secondary">
            {' '}
            av {max.toLocaleString('nb-NO')}
          </Typography>
        )}
      </Typography>
      <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.25 }}>
        siste {windowDays} dager{max == null ? ' — ingen grense' : overLimit ? ' — grensen er nådd' : ''}
      </Typography>
      <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.25 }}>
        {lastResetAt ? `Sist nullstilt ${formatDate(lastResetAt)}` : 'Aldri nullstilt'}
      </Typography>
    </Box>
  );
}
